import React from 'react';
import {Send, Users, Heart, Lightbulb, CheckCircle} from 'lucide-react';

/**
 * Peer assessment component for peer review of student work
 *
 * @param {Object} props
 * @param {Object} props.peerAssessmentForm - Form data for peer assessment
 * @param {Function} props.handlePeerAssessmentChange - Function to handle form field changes
 * @param {Function} props.handleFriendNeededChange - Function to handle "friend needed" multi-select changes
 * @param {Function} props.handlePeerAssessmentSubmit - Function to handle form submission
 * @param {Object} props.submission - Submission data if available
 */
const PeerAssessment = ({
                            peerAssessmentForm,
                            handlePeerAssessmentChange,
                            handleFriendNeededChange,
                            handlePeerAssessmentSubmit,
                            submission
                        }) => {
    // If already submitted, show the submitted assessment
    if (submission && submission.peer_assessment) {
        return (
            <div className="bg-white font-[poppins] rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D67C2C] shadow-lg">
                            <Users className="w-6 h-6 text-white"/>
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Peer Assessment Complete</h2>
                            <p className="text-gray-600 mt-1">Your friend's feedback has been submitted</p>
                        </div>
                    </div>
                </div>

                <div className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* "My friend liked doing this work" section */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100 shadow-sm">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center shadow-lg">
                                    <Heart className="w-5 h-5 text-white"/>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Friend's Enjoyment</h3>
                            </div>
                            
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 mb-4">My friend liked doing this work</p>
                                <div className="flex justify-center gap-6">
                                    <div className="flex flex-col items-center">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg
                        ${submission.peer_assessment.friend_liked === true ? 'bg-white border-2 border-green-500 scale-110' : 'bg-green-100'}`}>
                                            <span>😊</span>
                                        </div>
                                        <span className="mt-2 font-medium text-sm text-gray-700">Yes</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg
                        ${submission.peer_assessment.friend_liked === false ? 'bg-white border-2 border-green-500 scale-110' : 'bg-green-100'}`}>
                                            <span>😔</span>
                                        </div>
                                        <span className="mt-2 font-medium text-sm text-gray-700">No</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg
                        ${submission.peer_assessment.friend_liked === null ? 'bg-white border-2 border-green-500 scale-110' : 'bg-green-100'}`}>
                                            <span>🤔</span>
                                        </div>
                                        <span className="mt-2 font-medium text-sm text-gray-700 text-center">Don't<br/>Know</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* "My friend found this work easy" section */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100 shadow-sm">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center shadow-lg">
                                    <Lightbulb className="w-5 h-5 text-white"/>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Friend's Difficulty</h3>
                            </div>
                            
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 mb-4">My friend found this work easy</p>
                                <div className="flex justify-center gap-6">
                                    <div className="flex flex-col items-center">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg
                                            ${submission.peer_assessment.friend_easy === true ? 'bg-white border-2 border-purple-500 scale-110' : 'bg-purple-100'}`}>
                                            <span>😊</span>
                                        </div>
                                        <span className="mt-2 font-medium text-sm text-gray-700">Yes</span>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg
                                            ${submission.peer_assessment.friend_easy === false ? 'bg-white border-2 border-purple-500 scale-110' : 'bg-purple-100'}`}>
                                            <span>😔</span>
                                        </div>
                                        <span className="mt-2 font-medium text-sm text-gray-700">No</span>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg
                                            ${submission.peer_assessment.friend_easy === null ? 'bg-white border-2 border-purple-500 scale-110' : 'bg-purple-100'}`}>
                                            <span>🤔</span>
                                        </div>
                                        <span className="mt-2 font-medium text-sm text-gray-700 text-center">Don't<br/>Know</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* "To do this work, my friend needed..." section */}
                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-100 shadow-sm">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center shadow-lg">
                                    <Users className="w-5 h-5 text-white"/>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Friend's Support</h3>
                            </div>
                            
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 mb-4">To do this work, my friend needed...</p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="flex flex-col items-center">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all shadow-lg
                                            ${submission.peer_assessment.friend_needed.includes("classmate") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100'}`}>
                                            <span>👥</span>
                                        </div>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">Classmate</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all shadow-lg
                                            ${submission.peer_assessment.friend_needed.includes("teacher") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100'}`}>
                                            <span>👨‍🏫</span>
                                        </div>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">Teacher</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all shadow-lg
                                            ${submission.peer_assessment.friend_needed.includes("books") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100'}`}>
                                            <span>📚</span>
                                        </div>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">Books</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all shadow-lg
                                            ${submission.peer_assessment.friend_needed.includes("computer") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100'}`}>
                                            <span>💻</span>
                                        </div>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">Computer</span>
                                    </div>

                                    <div className="flex flex-col items-center col-span-2">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all shadow-lg
                                            ${submission.peer_assessment.friend_needed.includes("none") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100'}`}>
                                            <span>⛔</span>
                                        </div>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">None</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Otherwise, show the assessment form
    return (
        <div className="bg-white font-[poppins] rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D67C2C] shadow-lg">
                        <Users className="w-6 h-6 text-white"/>
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Peer Assessment</h2>
                        <p className="text-gray-600 mt-1">Ask a friend to review your work</p>
                    </div>
                </div>
            </div>

            <div className="p-8">
                <form onSubmit={handlePeerAssessmentSubmit}>
                    <div className="bg-blue-50 rounded-xl p-4 mb-8 border border-blue-200">
                        <p className="text-sm font-medium text-blue-800 mb-2">Instructions for your friend:</p>
                        <p className="text-sm text-blue-700">
                            Have a friend or classmate complete this section about your work. They should answer honestly based on what they observed.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* "My friend liked doing this work" section */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center shadow-lg">
                                    <Heart className="w-5 h-5 text-white"/>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Friend's Enjoyment</h3>
                            </div>
                            
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 mb-4">My friend liked doing this work</p>
                                <div className="flex justify-center gap-6">
                                    <div className="flex flex-col items-center">
                                        <button
                                            type="button"
                                            onClick={() => handlePeerAssessmentChange('friend_liked', true)}
                                            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg hover:scale-105
                        ${peerAssessmentForm.friend_liked === true ? 'bg-white border-2 border-green-500 scale-110' : 'bg-green-100 hover:bg-white hover:border-2 hover:border-green-300'}`}
                                        >
                                            <span>😊</span>
                                        </button>
                                        <span className="mt-2 font-medium text-sm text-gray-700">Yes</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <button
                                            type="button"
                                            onClick={() => handlePeerAssessmentChange('friend_liked', false)}
                                            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg hover:scale-105
                        ${peerAssessmentForm.friend_liked === false ? 'bg-white border-2 border-green-500 scale-110' : 'bg-green-100 hover:bg-white hover:border-2 hover:border-green-300'}`}
                                        >
                                            <span>😔</span>
                                        </button>
                                        <span className="mt-2 font-medium text-sm text-gray-700">No</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <button
                                            type="button"
                                            onClick={() => handlePeerAssessmentChange('friend_liked', null)}
                                            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg hover:scale-105
                        ${peerAssessmentForm.friend_liked === null ? 'bg-white border-2 border-green-500 scale-110' : 'bg-green-100 hover:bg-white hover:border-2 hover:border-green-300'}`}
                                        >
                                            <span>🤔</span>
                                        </button>
                                        <span className="mt-2 font-medium text-sm text-gray-700 text-center">Don't<br/>Know</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* "My friend found this work easy" section */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center shadow-lg">
                                    <Lightbulb className="w-5 h-5 text-white"/>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Friend's Difficulty</h3>
                            </div>
                            
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 mb-4">My friend found this work easy</p>
                                <div className="flex justify-center gap-6">
                                    <div className="flex flex-col items-center">
                                        <button
                                            type="button"
                                            onClick={() => handlePeerAssessmentChange('friend_easy', true)}
                                            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg hover:scale-105
                        ${peerAssessmentForm.friend_easy === true ? 'bg-white border-2 border-purple-500 scale-110' : 'bg-purple-100 hover:bg-white hover:border-2 hover:border-purple-300'}`}
                                        >
                                            <span>😊</span>
                                        </button>
                                        <span className="mt-2 font-medium text-sm text-gray-700">Yes</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <button
                                            type="button"
                                            onClick={() => handlePeerAssessmentChange('friend_easy', false)}
                                            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg hover:scale-105
                        ${peerAssessmentForm.friend_easy === false ? 'bg-white border-2 border-purple-500 scale-110' : 'bg-purple-100 hover:bg-white hover:border-2 hover:border-purple-300'}`}
                                        >
                                            <span>😔</span>
                                        </button>
                                        <span className="mt-2 font-medium text-sm text-gray-700">No</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <button
                                            type="button"
                                            onClick={() => handlePeerAssessmentChange('friend_easy', null)}
                                            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg hover:scale-105
                        ${peerAssessmentForm.friend_easy === null ? 'bg-white border-2 border-purple-500 scale-110' : 'bg-purple-100 hover:bg-white hover:border-2 hover:border-purple-300'}`}
                                        >
                                            <span>🤔</span>
                                        </button>
                                        <span className="mt-2 font-medium text-sm text-gray-700 text-center">Don't<br/>Know</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* "To do this work, my friend needed..." section */}
                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-100 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center shadow-lg">
                                    <Users className="w-5 h-5 text-white"/>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Friend's Support</h3>
                            </div>
                            
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 mb-4">To do this work, my friend needed... (Select all that apply)</p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="flex flex-col items-center">
                                        <button
                                            type="button"
                                            onClick={() => handleFriendNeededChange("classmate")}
                                            className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all shadow-lg hover:scale-105
                          ${peerAssessmentForm.friend_needed.includes("classmate") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100 hover:bg-white hover:border-2 hover:border-blue-300'}`}
                                        >
                                            <span>👥</span>
                                        </button>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">Classmate</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <button
                                            type="button"
                                            onClick={() => handleFriendNeededChange("teacher")}
                                            className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all shadow-lg hover:scale-105
                          ${peerAssessmentForm.friend_needed.includes("teacher") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100 hover:bg-white hover:border-2 hover:border-blue-300'}`}
                                        >
                                            <span>👨‍🏫</span>
                                        </button>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">Teacher</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <button
                                            type="button"
                                            onClick={() => handleFriendNeededChange("books")}
                                            className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all shadow-lg hover:scale-105
                          ${peerAssessmentForm.friend_needed.includes("books") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100 hover:bg-white hover:border-2 hover:border-blue-300'}`}
                                        >
                                            <span>📚</span>
                                        </button>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">Books</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <button
                                            type="button"
                                            onClick={() => handleFriendNeededChange("computer")}
                                            className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all shadow-lg hover:scale-105
                          ${peerAssessmentForm.friend_needed.includes("computer") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100 hover:bg-white hover:border-2 hover:border-blue-300'}`}
                                        >
                                            <span>💻</span>
                                        </button>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">Computer</span>
                                    </div>

                                    <div className="flex flex-col items-center col-span-2">
                                        <button
                                            type="button"
                                            onClick={() => handleFriendNeededChange("none")}
                                            className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all shadow-lg hover:scale-105
                          ${peerAssessmentForm.friend_needed.includes("none") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100 hover:bg-white hover:border-2 hover:border-blue-300'}`}
                                        >
                                            <span>⛔</span>
                                        </button>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">None</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit button */}
                    <div className="flex justify-center mt-8 pt-6 border-t border-gray-200">
                        <button
                            type="submit"
                            className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-xl shadow-lg text-white bg-[#D67C2C] hover:bg-[#c06a25] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D67C2C] transition-all hover:scale-105"
                        >
                            <Send size={20} className="mr-3"/>
                            Submit Peer Assessment
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PeerAssessment;